import { Box, Button, Field, HStack, Input, Stack, Text, VStack } from "@chakra-ui/react"
import { Alert } from "@chakra-ui/react"
import { useState, useCallback, useEffect } from "react"
import { authClient } from "./auth"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useLazyQuery, useMutation } from "@apollo/client"
import { AcceptInvitationAfterSignupDocument, ValidateInvitationDocument } from "@hydrologiq/gql-experience/graphql"
import Link from "@hydrologiq/shared/components/Link"

interface SignUpFormProps {
  invitationId?: string
  prefilledEmail?: string
}

export function SignUpForm({ invitationId, prefilledEmail }: SignUpFormProps = {}) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [name, setName] = useState("")
  const [email, setEmail] = useState(prefilledEmail || "")
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [invitationInfo, setInvitationInfo] = useState<{
    valid: boolean
    message: string
    organizationName?: string
    role?: string
  } | null>(null)

  // Get invitation details from URL if not passed as props
  const urlInvitationId = invitationId || searchParams.get("invitation") || undefined
  const urlEmail = prefilledEmail || searchParams.get("email") || ""

  // GraphQL hooks
  const [validateInvitation] = useLazyQuery(ValidateInvitationDocument)
  const [acceptInvitationAfterSignup] = useMutation(AcceptInvitationAfterSignupDocument)

  // Pre-fill email from URL parameters
  useEffect(() => {
    if (urlEmail && !email) {
      setEmail(urlEmail)
    }
  }, [urlEmail, email])

  // Validate invitation when email changes
  useEffect(() => {
    const validateInvitationAsync = async () => {
      if (!email || email.length < 3) {
        setInvitationInfo(null)
        return
      }

      try {
        const result = await validateInvitation({
          variables: {
            email,
            invitationId: urlInvitationId,
          },
        })

        if (result.data?.validateInvitation) {
          setInvitationInfo({
            valid: result.data.validateInvitation.valid,
            message: result.data.validateInvitation.message,
            organizationName: result.data.validateInvitation.organizationName || undefined,
            role: result.data.validateInvitation.role || undefined,
          })
        }
      } catch (err) {
        console.error("Failed to validate invitation:", err)
        setInvitationInfo({
          valid: false,
          message: "Failed to validate invitation",
        })
      }
    }

    const timeoutId = setTimeout(validateInvitationAsync, 500) // Debounce
    return () => clearTimeout(timeoutId)
  }, [email, urlInvitationId, validateInvitation])

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      setError(null)

      if (password !== confirmPassword) {
        setError("Passwords do not match")
        return
      }

      if (password.length < 8) {
        setError("Password must be at least 8 characters long")
        return
      }

      // Check if invitation is valid before proceeding
      if (!invitationInfo?.valid) {
        setError("You must have a valid invitation to create an account")
        return
      }

      setLoading(true)

      try {
        // Step 1: Create user account using Better Auth directly
        const signupResult = await authClient.signUp.email({
          email,
          password,
          name,
          callbackURL: "/",
        })

        if (signupResult.error) {
          // Check if this is an existing user case
          const errorMessage = signupResult.error.message || "Failed to create account"

          if (errorMessage.includes("already exists") || errorMessage.includes("User already exists")) {
            // Redirect to sign in with invitation info
            const signInUrl = new URL("/auth/sign-in", window.location.origin)
            if (urlInvitationId) {
              signInUrl.searchParams.set("invitation", urlInvitationId)
            }
            signInUrl.searchParams.set("email", email)
            signInUrl.searchParams.set("message", "Please sign in with your existing account to accept the invitation.")
            navigate(signInUrl.pathname + signInUrl.search)
            return
          }

          setError(errorMessage)
          return
        }

        if (!signupResult.data?.user?.id) {
          setError("Account creation failed - no user ID returned")
          return
        }

        // Now accept the invitation via GraphQL (user is authenticated)
        if (urlInvitationId) {
          const invitationResult = await acceptInvitationAfterSignup({
            variables: {
              invitationId: urlInvitationId,
            },
          })

          if (!invitationResult.data?.acceptInvitationAfterSignup?.success) {
            setError(invitationResult.data?.acceptInvitationAfterSignup?.message || "Failed to accept invitation")
            return
          }
        }

        // Step 3: Explicitly sign in to ensure session is properly established
        const signInResult = await authClient.signIn.email({
          email,
          password,
        })

        if (signInResult.error) {
          setError(signInResult.error.message || "Failed to sign in after account creation")
          return
        }

        // Step 4: Hard reload to ensure AuthProvider picks up the new session
        window.location.reload()
      } catch (err: any) {
        console.error("Signup error:", err)
        setError(err.message || "An unexpected error occurred")
      } finally {
        setLoading(false)
      }
    },
    [name, email, password, confirmPassword, navigate, invitationInfo, acceptInvitationAfterSignup, urlInvitationId]
  )

  return (
    <Box as="form" onSubmit={handleSubmit} w="full" maxW="md" bg="white" borderRadius="md" boxShadow="md">
      <VStack gap={6} align="stretch">
        <Field.Root required>
          <Field.Label>Name</Field.Label>
          <Input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter your name"
            disabled={loading}
          />
        </Field.Root>

        <Field.Root required>
          <Field.Label>Email</Field.Label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            disabled={loading}
          />
          {invitationInfo && (
            <Alert.Root status={invitationInfo.valid ? "success" : "error"} size="sm" mt={2}>
              <Alert.Content>
                <Alert.Description>
                  <HStack gap={1} width={"100%"}>
                    {invitationInfo.message}
                    {invitationInfo.valid && invitationInfo.organizationName && (
                      <HStack>
                        <Text as="span" fontWeight="">
                          for {invitationInfo.organizationName}
                          {invitationInfo.role && ` as ${invitationInfo.role}`}
                        </Text>
                        <Text></Text>
                      </HStack>
                    )}
                  </HStack>
                </Alert.Description>
              </Alert.Content>
            </Alert.Root>
          )}
        </Field.Root>

        <Field.Root required>
          <Field.Label>Password</Field.Label>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password"
            disabled={loading}
          />
        </Field.Root>

        <Field.Root required>
          <Field.Label>Confirm Password</Field.Label>
          <Input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm your password"
            disabled={loading}
          />
        </Field.Root>

        {error && (
          <Text color="error" fontSize="sm">
            {error}
          </Text>
        )}

        <Button type="submit" variant="hydrologiq_primary" w="full" disabled={loading || !invitationInfo?.valid}>
          {loading ? "Creating account..." : "Create Account"}
        </Button>

        <Stack direction="row" justify="center" gap={1} fontSize="sm">
          <Text color="primary_light">Already have an account?</Text>
          <Link href="/auth/sign-in" variant={"primary" as any} internal>
            Sign in
          </Link>
        </Stack>
      </VStack>
    </Box>
  )
}
